// Voice of Dubai — step screen (new design). Task widgets live in ./tasks.js.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getSteps, updateProgress, logAnswer, submitReview } from '../../api';
import { getMeta } from '../../offlineStorage';
import { t, useLang } from '../../i18n';
import {
  normalizeAnswer, renderFormattedText, useOfflineUrl, FullscreenViewer, SwipeQuiz, PuzzleBoard, SortTask, DishTask,
  PhraseTask, OrderTask, PostcardMakerNew, PairsTask, MemorySequence, IceBreak, ScratchCard, BeforeAfter, SwipeGallery,
} from './tasks';
import { Icon, LogoTile, SegProgress, RouteMap, googleDirectionsUrl, useMyPosition } from '../vod/Kit';
import '../../styles/vod.css';
import '../vod/screens.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const BASE = API_URL.replace('/api', '');

const fmt = (s) => (isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}` : '0:00');
const PlayIcon = ({ size = 24 }) => <svg width={size} height={size} viewBox="0 0 24 24"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.2-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" fill="#fff" /></svg>;
const PauseIcon = ({ size = 22 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="#fff"><rect x="6" y="5" width="4" height="14" rx="1.2" /><rect x="14" y="5" width="4" height="14" rx="1.2" /></svg>;
const SPEEDS = [1, 1.5, 2];

// Audio player: "big" = main story player, otherwise compact row
function VodAudio({ rawUrl, title, big }) {
  const src = useOfflineUrl(rawUrl ? BASE + rawUrl : '');
  const ref = useRef(null);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const [paused, setPaused] = useState(true);
  const [speed, setSpeed] = useState(0);
  const [drag, setDrag] = useState(false);

  useEffect(() => {
    const a = ref.current; if (!a) return;
    const upd = () => { if (!drag) setPos(a.currentTime); setDur(a.duration || 0); setPaused(a.paused); };
    ['timeupdate', 'play', 'pause', 'ended', 'loadedmetadata', 'durationchange'].forEach(e => a.addEventListener(e, upd));
    return () => ['timeupdate', 'play', 'pause', 'ended', 'loadedmetadata', 'durationchange'].forEach(e => a.removeEventListener(e, upd));
  }, [drag]);

  const toggle = () => {
    const a = ref.current; if (!a) return;
    if (a.paused) {
      document.querySelectorAll('audio').forEach(o => { if (o !== a) try { o.pause(); } catch (e) {} });
      a.play().catch(() => {});
    } else a.pause();
  };
  const seek = (v) => { const a = ref.current; if (a) a.currentTime = v; setPos(v); };
  const nextSpeed = () => { const n = (speed + 1) % SPEEDS.length; setSpeed(n); if (ref.current) ref.current.playbackRate = SPEEDS[n]; };
  const p = dur ? (pos / dur) * 100 : 0;
  const seekEl = (
    <input className="vods-seek seek" type="range" min="0" max={dur || 1} step="0.1" value={pos}
      style={{ '--p': p + '%' }} aria-label="seek"
      onChange={e => { setDrag(true); seek(+e.target.value); }} onPointerUp={() => setDrag(false)} onTouchEnd={() => setDrag(false)} />
  );

  if (big) return (
    <div className="vods-player">
      <audio ref={ref} src={src} preload="metadata" />
      <span className="time cur">{fmt(pos)}</span>
      <span className="time dur">{fmt(dur)}</span>
      {seekEl}
      <button className="vods-pbtn play" onClick={toggle} aria-label="play">{paused ? <PlayIcon /> : <PauseIcon />}</button>
      <button className="vods-speed speed" onClick={nextSpeed}>{SPEEDS[speed]}×</button>
      {title && <div className="ttl" dir="auto">{title}</div>}
    </div>
  );
  return (
    <div className="vods-mini">
      <audio ref={ref} src={src} preload="metadata" />
      <button className="vods-pbtn sm" onClick={toggle} aria-label="play">{paused ? <PlayIcon size={20} /> : <PauseIcon size={18} />}</button>
      <div className="info">
        <small dir="auto">{title || t('narration')}</small>
        <div className="row">{seekEl}<span className="time">{fmt(pos)} / {fmt(dur)}</span></div>
      </div>
      <button className="vods-speed" onClick={nextSpeed}>{SPEEDS[speed]}×</button>
    </div>
  );
}

const TASK_TYPES = ['swipe', 'puzzle', 'sort', 'dish', 'order', 'beforeafter', 'scratch', 'memseq', 'ice', 'pairs', 'postcard', 'phrase'];

export default function StepPage() {
  const lang = useLang();
  const navigate = useNavigate();
  const { excursionId, stepNum } = useParams();
  const currentStepNum = parseInt(stepNum);

  const [steps, setSteps] = useState([]);
  const [totalSteps, setTotalSteps] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offlineMode, setOfflineMode] = useState(false);
  const [meta, setMeta] = useState({});

  const [answer, setAnswer] = useState('');
  const [picked, setPicked] = useState(null);
  const [result, setResult] = useState(null); // null | 'ok' | 'bad'
  const [showHint, setShowHint] = useState(false);
  const [attemptedWrong, setAttemptedWrong] = useState(false);
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [exhausted, setExhausted] = useState(false);
  const [attemptsInfo, setAttemptsInfo] = useState('');
  const [revealShown, setRevealShown] = useState(false);
  const [open, setOpen] = useState({}); // accordions: fact, video, map
  const [fullscreen, setFullscreen] = useState(null);

  const [showFinish, setShowFinish] = useState(false);
  const [finalScore, setFinalScore] = useState({ earned: 0, max: 0 });
  const [showReview, setShowReview] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);

  const loadSteps = useCallback(async () => {
    try {
      const data = await getSteps(excursionId);
      setSteps(data.steps || []);
      setTotalSteps(data.total_steps || (data.steps || []).length);
      setMeta(data);
      setOfflineMode(false);
    } catch (e) {
      try {
        const cached = await getMeta(excursionId);
        if (cached && cached.steps) {
          setSteps(cached.steps); setTotalSteps(cached.total_steps || cached.steps.length); setMeta(cached); setOfflineMode(true);
        }
      } catch (e2) {}
    }
    setLoading(false);
  }, [excursionId]);

  useEffect(() => { loadSteps(); }, [loadSteps, lang]);

  useEffect(() => {
    setAnswer(''); setPicked(null); setResult(null); setShowHint(false); setAttemptedWrong(false);
    setAttemptsUsed(0); setExhausted(false); setAttemptsInfo(''); setRevealShown(false); setOpen({});
    window.scrollTo(0, 0);
  }, [stepNum]);

  useEffect(() => {
    if (steps.length > 0 && currentStepNum > 0) updateProgress(excursionId, currentStepNum, false).catch(() => {});
  }, [excursionId, currentStepNum, steps.length]);

  const step = steps.find(s => s.step_number === currentStepNum) || steps[currentStepNum - 1] || {};
  const stepsCount = totalSteps || steps.length;
  const stepImageUrl = useOfflineUrl(step.image_url ? BASE + step.image_url : '');
  const stepVideoUrl = useOfflineUrl(step.video_url ? BASE + step.video_url : '');
  const revealImageUrl = useOfflineUrl(step.reveal_image ? BASE + step.reveal_image : '');
  const hasPoints = !!meta.has_points;
  const { pos: myPos, status: locStatus, refresh: refreshLoc } = useMyPosition(!!open.map);

  const sendReview = async () => {
    if (rating < 1) return;
    setReviewBusy(true);
    try { await submitReview(excursionId, rating, comment); } catch (e) {}
    setReviewBusy(false);
    navigate('/excursions');
  };

  if (loading) return <div className="vod vod-loading">{t('loading')}</div>;
  if (steps.length === 0) return <div className="vod vod-loading">{t('steps_not_loaded')}</div>;

  // ---------- review ----------
  if (showReview) {
    const labels = t('rate_labels');
    return (
      <main className="vod vod-screen">
        <div className="vod-hero" style={{ height: '100%', backgroundImage: meta.final_image_url ? `url(${BASE + meta.final_image_url})` : 'url(/auth-hero.jpg)' }} />
        <div className="vod-top" style={{ minHeight: 160, justifyContent: 'center' }}><LogoTile size="md" /></div>
        <section className="vod-card" style={{ textAlign: 'center' }}>
          <h1 className="vod-h1" style={{ fontSize: 26 }}>{t('rate_title')}</h1>
          <p className="vod-text" style={{ margin: '6px 0 0' }}>{t('rate_sub')}</p>
          <div className="vodr-stars">{[1, 2, 3, 4, 5].map(n => <button key={n} className={n <= rating ? 'on' : ''} onClick={() => setRating(n)} aria-label={String(n)}>★</button>)}</div>
          <div style={{ minHeight: 22, fontWeight: 600, color: 'var(--vod-gold)', marginBottom: 12 }}>{rating > 0 ? labels[rating] : ''}</div>
          <textarea className="vodr-ta" maxLength={500} placeholder={t('comment_ph')} value={comment} onChange={e => setComment(e.target.value)} dir="auto" />
          <button className="vod-btn" style={{ marginTop: 14 }} disabled={rating < 1 || reviewBusy} onClick={sendReview}>{reviewBusy ? t('sending') : t('send_review')}</button>
          <button className="vod-btn2" onClick={() => navigate('/excursions')}>{t('skip')}</button>
        </section>
      </main>
    );
  }

  // ---------- finish ----------
  if (showFinish) {
    return (
      <main className="vod vodf" style={{ backgroundImage: meta.final_image_url ? `url(${BASE + meta.final_image_url})` : 'url(/auth-hero.jpg)' }}>
        <div style={{ position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 44px)', left: 0, right: 0, display: 'flex', justifyContent: 'center' }}><LogoTile size="md" /></div>
        <section className="vod-card" style={{ margin: '0 14px calc(env(safe-area-inset-bottom, 0px) + 14px)' }}>
          <div className="vod-accent" style={{ margin: '0 auto 14px' }} />
          <h1 className="vod-h1" dir="auto">{meta.finish_title || t('congrats')}</h1>
          <p className="vod-text" style={{ margin: '6px 0 0' }} dir="auto">{meta.finish_subtitle || t('finished_sub')}</p>
          {hasPoints && <div className="vodf-score">{finalScore.earned} <span>/ {finalScore.max} {t('points')}</span></div>}
          {meta.partner_logo && (
            <div style={{ margin: '16px 0 4px' }}>
              <div style={{ fontSize: 13, color: 'var(--vod-muted)', marginBottom: 8 }}>{t('thanks_partner')}</div>
              <img src={BASE + meta.partner_logo} alt="" style={{ maxWidth: 160, maxHeight: 80, objectFit: 'contain' }} />
            </div>
          )}
          <div style={{ height: 14 }} />
          {!meta.finish_hide_review && <button className="vod-btn" onClick={() => { setShowFinish(false); setShowReview(true); }}>★ {t('rate')}</button>}
          <button className="vod-btn2" onClick={() => navigate('/excursions')}>{t('to_excursions')}</button>
        </section>
      </main>
    );
  }

  if (!step.id) return <div className="vod vod-loading">{t('step_not_found')}</div>;

  // ---------- step data ----------
  const audioList = (Array.isArray(step.audios) && step.audios.length > 0)
    ? step.audios.filter(a => a && a.url)
    : (step.audio_url ? [{ url: step.audio_url, title: '' }] : []);
  const swipeCards = (Array.isArray(step.swipe_cards) && step.swipe_cards.length > 0)
    ? step.swipe_cards.filter(c => c && c.text)
    : (step.question_type === 'swipe' && step.question ? [{ text: step.question, answer: (step.answers && step.answers[0]) || 'верю', image: '' }] : []);
  const qt = step.question_type;
  const cfg = {
    puzzle: qt === 'puzzle' && step.puzzle && step.puzzle.image ? { image: step.puzzle.image, size: parseInt(step.puzzle.size) || 3 } : null,
    sort: qt === 'sort' && step.sort_data && (step.sort_data.categories || []).length && (step.sort_data.items || []).length ? step.sort_data : null,
    dish: qt === 'dish' && step.dish_data && (step.dish_data.items || []).length ? step.dish_data : null,
    order: qt === 'order' && Array.isArray(step.order_items) && step.order_items.length ? step.order_items : null,
    beforeafter: qt === 'beforeafter' && step.beforeafter && step.beforeafter.before && step.beforeafter.after ? step.beforeafter : null,
    scratch: qt === 'scratch' && step.scratch && step.scratch.image ? step.scratch : null,
    memseq: qt === 'memseq' && step.memseq && (parseInt(step.memseq.length) || 0) >= 2 ? step.memseq : null,
    ice: qt === 'ice' && step.ice && step.ice.image ? step.ice : null,
    pairs: qt === 'pairs' && step.pairs_data && (step.pairs_data.pairs || []).length ? step.pairs_data.pairs : null,
    postcard: qt === 'postcard' && step.postcard_data && (step.postcard_data.backgrounds || []).length ? step.postcard_data : null,
    phrase: qt === 'phrase' && step.phrase_data && (step.phrase_data.cards || []).length ? step.phrase_data : null,
  };
  const isTask = TASK_TYPES.includes(qt);
  const isRequired = !!step.is_required;
  const maxAttempts = isRequired ? 0 : (parseInt(step.max_attempts) || 0);
  const hasQuestion = step.has_question && (
    (qt === 'swipe' && swipeCards.length) || (isTask && qt !== 'swipe' && cfg[qt]) ||
    (!isTask && (step.question || (qt === 'choice' && (step.answer_options || []).length)))
  );
  const isLast = currentStepNum >= stepsCount;

  // ---------- scoring ----------
  const attemptId = (() => {
    const k = `run_${excursionId}`;
    let r = null;
    try { r = localStorage.getItem(k); } catch (e) {}
    if (!r) { r = String(Date.now()); try { localStorage.setItem(k, r); } catch (e) {} }
    return r;
  })();
  const logA = (payload) => logAnswer(excursionId, Object.assign({ attempt_id: attemptId, step_id: step.id, step_number: step.step_number, used_hint: false }, payload)).catch(() => {});
  const scoreKey = `quest_${excursionId}`;
  const getQS = () => { try { return JSON.parse(localStorage.getItem(scoreKey)) || { score: 0, scored: [], hinted: [] }; } catch (e) { return { score: 0, scored: [], hinted: [] }; } };
  const saveQS = (st) => { try { localStorage.setItem(scoreKey, JSON.stringify(st)); } catch (e) {} };
  const award = (id) => {
    if (!hasPoints) return;
    const st = getQS();
    if (!st.scored.includes(id)) { st.score += parseInt(step.points) || 0; st.scored.push(id); saveQS(st); }
  };
  const computeMax = () => steps.reduce((sum, s) => {
    if (!s.has_question) return sum;
    const pts = parseInt(s.points) || 0;
    if (s.question_type === 'swipe') return sum + pts * ((Array.isArray(s.swipe_cards) && s.swipe_cards.length) || (s.question ? 1 : 0));
    if (s.question_type === 'sort') return sum + pts * ((s.sort_data && (s.sort_data.items || []).length) || 0);
    if (s.question_type === 'pairs') return sum + pts * ((s.pairs_data && (s.pairs_data.pairs || []).length) || 0);
    return sum + pts;
  }, 0);

  const stopAllAudio = () => document.querySelectorAll('audio, video').forEach(a => { try { a.pause(); } catch (e) {} });
  const finishExcursion = () => {
    stopAllAudio();
    updateProgress(excursionId, currentStepNum, true).catch(() => {});
    if (hasPoints) setFinalScore({ earned: getQS().score, max: computeMax() });
    setShowFinish(true);
  };
  const goNext = () => {
    if (isLast) { finishExcursion(); return; }
    stopAllAudio();
    navigate(`/excursion/${excursionId}/step/${currentStepNum + 1}`, { replace: true });
  };
  const hasReveal = !!(step.reveal_image || step.reveal_text);
  const advanceOrReveal = () => {
    if (step.has_question && hasReveal && !revealShown) { stopAllAudio(); setRevealShown(true); window.scrollTo(0, 0); return; }
    goNext();
  };

  const checkAnswer = (explicit) => {
    if (exhausted || result === 'ok') return;
    const given = ((explicit != null ? explicit : answer) || '').trim();
    if (!given) return;
    const ok = (step.answers || []).some(a => normalizeAnswer(a) === normalizeAnswer(given));
    logA({ question: step.question || '', answer_given: given, is_correct: ok, used_hint: showHint });
    if (ok) {
      award(step.id);
      setResult('ok'); setAttemptsInfo(''); setExhausted(false);
      setTimeout(() => advanceOrReveal(), 1400);
    } else {
      setResult('bad'); setAttemptedWrong(true);
      if (!(hasPoints && step.hint_penalty > 0)) setShowHint(true);
      if (maxAttempts > 0) {
        const used = attemptsUsed + 1; setAttemptsUsed(used);
        const left = maxAttempts - used;
        if (left <= 0) { setExhausted(true); setAttemptsInfo(t('attempts_over')); }
        else setAttemptsInfo(t('attempts_left', { n: left }));
      }
    }
  };
  const openHint = () => {
    if (hasPoints && step.hint_penalty) {
      const st = getQS();
      if (!st.hinted.includes(step.id)) { st.score -= parseInt(step.hint_penalty) || 0; st.hinted.push(step.id); saveQS(st); }
    }
    setShowHint(true);
  };
  const revealAnswer = () => {
    logA({ question: step.question || step.title || '', answer_given: '__reveal_requested__', is_correct: false, used_hint: true });
    setAttemptedWrong(true); setRevealShown(true); stopAllAudio(); window.scrollTo(0, 0);
  };

  const solved = (label, id) => () => { logA({ question: step.question || label, answer_given: 'solved', is_correct: true }); if (id) award(`${step.id}-${id}`); };
  const imgTap = (u) => setFullscreen({ images: [u], startIndex: 0 });

  // ---------- task widget ----------
  const renderTask = () => {
    const k = currentStepNum;
    switch (qt) {
      case 'swipe': return <SwipeQuiz key={k} cards={swipeCards} onCard={(idx, card, given, ok) => { logA({ question: card.text, answer_given: given, is_correct: ok }); if (ok) award(`${step.id}-c${idx}`); }} onDone={advanceOrReveal} />;
      case 'puzzle': return <PuzzleBoard key={k} image={cfg.puzzle.image} size={cfg.puzzle.size} onSolved={() => { solved('Puzzle', 'puzzle')(); advanceOrReveal(); }} />;
      case 'sort': return <SortTask key={k} categories={cfg.sort.categories} items={cfg.sort.items} baseUrl={BASE} maxAttempts={maxAttempts} onItemCorrect={(idx) => award(`${step.id}-s${idx}`)} onSolved={solved('Sort')} onDone={advanceOrReveal} />;
      case 'dish': return <DishTask key={k} containerTitle={cfg.dish.container_title} items={cfg.dish.items} baseUrl={BASE} maxAttempts={maxAttempts} onSolved={solved('Dish', 'dish')} onDone={advanceOrReveal} />;
      case 'memseq': return <MemorySequence key={k} length={parseInt(cfg.memseq.length) || 4} revealImage={cfg.memseq.reveal_image} revealText={cfg.memseq.reveal_text} maxAttempts={maxAttempts} onSolved={solved('Memory', 'mem')} onDone={advanceOrReveal} />;
      case 'postcard': return <PostcardMakerNew key={k} backgrounds={cfg.postcard.backgrounds} stickers={cfg.postcard.stickers || []} baseUrl={BASE} onDone={advanceOrReveal} />;
      case 'pairs': return <PairsTask key={k} pairs={cfg.pairs} baseUrl={BASE} backImage={step.pairs_data && step.pairs_data.back_image} onMatched={(pid) => award(`${step.id}-pair${pid}`)} onSolved={solved('Pairs')} onDone={advanceOrReveal} />;
      case 'ice': return <IceBreak key={k} image={cfg.ice.image} caption={cfg.ice.caption} taps={cfg.ice.taps} onDone={advanceOrReveal} />;
      case 'scratch': return <ScratchCard key={k} image={cfg.scratch.image} coverLabel={cfg.scratch.cover_label} caption={cfg.scratch.caption} onDone={advanceOrReveal} />;
      case 'beforeafter': return <BeforeAfter key={k} before={cfg.beforeafter.before} after={cfg.beforeafter.after} beforeLabel={cfg.beforeafter.before_label} afterLabel={cfg.beforeafter.after_label} onDone={advanceOrReveal} />;
      case 'order': return <OrderTask key={k} items={cfg.order} baseUrl={BASE} maxAttempts={maxAttempts} onSolved={solved('Order', 'order')} onDone={advanceOrReveal} onImageTap={imgTap} />;
      case 'phrase': return <PhraseTask key={k} cards={cfg.phrase.cards} extras={cfg.phrase.extras || []} baseUrl={BASE} maxAttempts={maxAttempts} onSolved={solved('Phrase', 'phrase')} onDone={advanceOrReveal} onImageTap={imgTap} />;
      default: return null;
    }
  };

  const point = step.lat && step.lng ? [{ lat: parseFloat(step.lat), lng: parseFloat(step.lng), title: step.title }] : [];
  const hasFact = step.has_fun_fact && ((step.fun_fact_images || []).length > 0 || step.fun_fact_text);
  const hasVideo = step.has_video && step.video_url;
  const toggle = (key) => setOpen(o => ({ ...o, [key]: !o[key] }));
  const heroH = step.image_url ? 340 : 200;

  // bottom buttons
  let primary = null;
  if (revealShown) primary = { label: isLast ? t('finish') : t('continue'), onClick: advanceOrReveal };
  else if (!hasQuestion) primary = { label: isLast ? t('finish') : t('continue'), onClick: goNext };
  else if (!isTask && qt !== 'choice' && !exhausted && result !== 'ok') primary = { label: t('check_answer'), onClick: () => checkAnswer() };
  else if (!isTask && qt === 'choice' && !exhausted && result !== 'ok') primary = { label: t('check_answer'), onClick: () => picked != null && checkAnswer(step.answer_options[picked]), disabled: picked == null };
  const showNext = hasQuestion && !revealShown && !isTask && (attemptedWrong || exhausted) && !isRequired && result !== 'ok';

  return (
    <main className="vod vod-screen">
      <div className="vod-hero" style={{ height: heroH, backgroundImage: stepImageUrl ? `url(${stepImageUrl})` : 'linear-gradient(160deg, #24305E, #B8893A)', cursor: stepImageUrl ? 'zoom-in' : 'default' }}
        onClick={() => stepImageUrl && setFullscreen({ images: [stepImageUrl], startIndex: 0 })} />
      <div className="vod-top vod-top-pass" style={{ minHeight: heroH - 66 }}>
        <Link className="vod-round icon back" to={`/excursion/${excursionId}`} aria-label="back">{Icon.back()}</Link>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
          <LogoTile size="sm" />
          {stepImageUrl && (
            <button type="button" className="vod-round icon" aria-label={t('expand')} title={t('expand')}
              onClick={() => setFullscreen({ images: [stepImageUrl], startIndex: 0 })}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0F1A3A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
            </button>
          )}
        </div>
      </div>

      <section className="vod-card">
        <SegProgress total={stepsCount} current={currentStepNum} />
        <div className="vod-stepn">{t('step_of', { n: currentStepNum, total: stepsCount })}</div>
        {offlineMode && <div className="vod-hint" style={{ fontSize: 13 }}>📴 {t('offline')}</div>}
        <h1 className="vod-h1" dir="auto">{step.title || t('step_of', { n: currentStepNum, total: stepsCount })}</h1>
        {step.location_label && <div className="vod-area" dir="auto">{step.location_label}</div>}
        {!step.location_label && <div style={{ height: 12 }} />}

        {revealShown ? (
          <div className="vods-reveal">
            <b>{t('correct_answer')}</b>
            {step.reveal_image && revealImageUrl && <img src={revealImageUrl} alt="" onClick={() => imgTap(revealImageUrl)} />}
            {step.reveal_text ? <p className="vod-text" style={{ margin: '8px 0 0' }} dir="auto">{step.reveal_text}</p>
              : (!step.reveal_image && (step.answers || [])[0] && <p className="vod-text" style={{ margin: '8px 0 0' }} dir="auto">{step.answers[0]}</p>)}
          </div>
        ) : (<>
          {audioList.map((a, i) => <VodAudio key={`${currentStepNum}-${i}-${a.url}`} rawUrl={a.url} title={a.title} big={i === 0 && !hasVideo} />)}

          {step.text && <div className="vod-text" dir="auto">{renderFormattedText(step.text)}</div>}

          {hasVideo && (
            <>
              <video className="vods-video" src={stepVideoUrl} controls playsInline preload="metadata" />
            </>
          )}

          {hasFact && (
            <>
              <button type="button" className={`vods-acc ${open.fact ? 'open' : ''}`} onClick={() => toggle('fact')}>
                {Icon.bulb()}<span className="t">{t('fun_fact')}</span>
                <span className="chev">{Icon.down({ size: 20, sw: 1.8 })}</span>
              </button>
              {open.fact && (
                <div className="vods-acc-body">
                  {(step.fun_fact_images || []).length > 0 && <SwipeGallery images={step.fun_fact_images} baseUrl={BASE} onImageTap={(urls, i) => setFullscreen({ images: urls, startIndex: i })} />}
                  {step.fun_fact_text && <div className="vod-text" dir="auto">{renderFormattedText(step.fun_fact_text)}</div>}
                </div>
              )}
            </>
          )}

          {hasQuestion && (
            <div className="vods-task">
              {qt !== 'swipe' && step.question && <p className="vods-question" dir="auto">{step.question}</p>}
              {isTask ? renderTask() : qt === 'choice' ? (<>
                <div className={`vod-hint ${result === 'ok' ? 'ok' : result === 'bad' ? 'bad' : ''}`}>
                  <b className="ic">{result === 'ok' ? '✓' : result === 'bad' ? '✕' : '?'}</b>
                  <span>{result === 'ok' ? t('correct') : result === 'bad' ? t('wrong') : t('select_one')}</span>
                </div>
                <div className="vods-opts">
                  {step.answer_options.map((opt, i) => {
                    const right = result === 'ok' && picked === i;
                    const wrong = result === 'bad' && picked === i;
                    return (
                      <button key={i} className={`vods-opt ${right ? 'right' : wrong ? 'wrong' : picked === i ? 'sel' : ''}`} disabled={exhausted || result === 'ok'}
                        onClick={() => { setPicked(i); if (result === 'bad') setResult(null); }}>
                        <span dir="auto">{opt}</span><i className="radio" />
                      </button>
                    );
                  })}
                </div>
              </>) : (<>
                {result && <div className={`vod-hint ${result === 'ok' ? 'ok' : 'bad'}`}><b className="ic">{result === 'ok' ? '✓' : '✕'}</b><span>{result === 'ok' ? t('correct') : t('wrong')}</span></div>}
                <label className={`vod-field ${result === 'bad' ? 'err' : ''}`}>
                  <input type="text" placeholder={t('your_answer')} value={answer} disabled={exhausted || result === 'ok'} dir="auto"
                    onChange={e => { setAnswer(e.target.value); if (result === 'bad') setResult(null); }} onKeyDown={e => e.key === 'Enter' && checkAnswer()} />
                </label>
              </>)}
              {attemptsInfo && <p className="vod-err" style={{ marginTop: 10 }}>{attemptsInfo}</p>}
              {attemptedWrong && step.hint && !showHint && hasPoints && step.hint_penalty > 0 && (
                <button className="vod-btn2" onClick={openHint}>💡 {t('show_hint', { n: step.hint_penalty })}</button>
              )}
              {showHint && step.hint && <div className="vod-hint" style={{ marginTop: 10 }}><b className="ic">!</b><span dir="auto"><b>{t('hint')}:</b> {step.hint}</span></div>}
              {step.allow_reveal && result !== 'ok' && !exhausted && (
                <button className="vod-btn2" onClick={revealAnswer}>🎯 {t('reveal_answer')}</button>
              )}
            </div>
          )}
        </>)}

        {open.map && point.length > 0 && (
          <div style={{ margin: '14px 0 0' }}>
            <RouteMap points={myPos ? [myPos, ...point] : point} height={260} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 8, fontSize: 13, color: 'var(--vod-muted)' }}>
              <span>{locStatus === 'ok' ? t('you_here') : locStatus === 'asking' ? t('loc_asking') : locStatus === 'denied' ? t('loc_denied') : locStatus === 'unavailable' ? t('loc_unavailable') : ''}</span>
              {locStatus !== 'asking' && (
                <button type="button" onClick={refreshLoc} style={{ border: 0, background: 'none', color: 'var(--vod-gold)', fontWeight: 600, font: 'inherit', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', padding: 0 }}>↻ {t('loc_refresh')}</button>
              )}
            </div>
          </div>
        )}

        <div style={{ height: 18 }} />
        {primary && (
          <button className="vod-btn" onClick={primary.onClick} disabled={primary.disabled}>{primary.label}{Icon.arrow()}</button>
        )}
        {showNext && <button className="vod-btn2" onClick={advanceOrReveal}>{t('next')}</button>}
        {point.length > 0 && (
          open.map
            ? <a className="vod-btn2" href={googleDirectionsUrl(point)} target="_blank" rel="noreferrer">{Icon.pin()}{t('open_in_google')}</a>
            : <button className="vod-btn2" onClick={() => toggle('map')}>{Icon.pin()}{t('view_on_map')}</button>
        )}
        {step.map_image_url && !point.length && (
          <button className="vod-btn2" onClick={() => imgTap(BASE + step.map_image_url)}>{Icon.map()}{t('view_on_map')}</button>
        )}
      </section>

      {fullscreen && <FullscreenViewer images={fullscreen.images} startIndex={fullscreen.startIndex} onClose={() => setFullscreen(null)} />}
    </main>
  );
}
